import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { Plus, Eye, Upload, Archive, Trash2, Download, AlertTriangle, Pencil, UserCheck, X } from "lucide-react";
import api from "../utils/api";
import { CLIENT_SERVICES } from "../utils/helpers";
import { downloadExport } from "../utils/exportUtils";
import { PageHeader, SearchBar, StatusBadge, Modal, FormField } from "../components/Table";
import { EditableCell, useExcelNav, patchRows } from "../components/gridShared";
import ClientImport from "../components/ClientImport";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";

const ENTITY_TYPES = ["Individual", "HUF", "Firm", "LLP", "Pvt Ltd", "Public Ltd", "Trust", "AOP"];
const CONSTITUTIONS = ["Proprietorship", "Partnership", "Company", "LLP"];
const SERVICES = CLIENT_SERVICES;
const STATUSES = ["Active", "Inactive", "Prospect"];

// Excel-grid column order (keyboard navigation follows this)
const GRID_COLS = ["client_code", "name", "entity_type", "pan", "mobile", "email", "assigned_staff_id", "status"];
const COL_LABELS = { client_code: "Code", name: "Client Name", entity_type: "Type", pan: "PAN", mobile: "Mobile", email: "Email", assigned_staff_id: "Assigned To", status: "Status" };
// Sort by the visible value, not the raw id
const SORT_KEYS = { assigned_staff_id: "staff_name" };

// ── Excel-style client grid ─────────────────────────────────────────────────────
function ClientGrid({ clients, staff, canEdit, showSelect, selected, setSelected, onCommit }) {
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const lastIdxRef = useRef(null);

  const sorted = useMemo(() => sortKey ? [...clients].sort((a, b) => {
    const k = SORT_KEYS[sortKey] || sortKey;
    const va = a[k] ?? ""; const vb = b[k] ?? "";
    return sortDir === "asc" ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
  }) : clients, [clients, sortKey, sortDir]);

  const toggleSelect = id => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const nav = useExcelNav({ rows: sorted, cols: GRID_COLS, canEdit, onToggleSelect: toggleSelect });

  const onSort = key => { setSortDir(d => sortKey === key ? (d === "asc" ? "desc" : "asc") : "asc"); setSortKey(key); };

  // Shift-click selects the whole range since the last clicked checkbox (display order)
  const handleCheckboxClick = (e, row, idx) => {
    e.stopPropagation();
    if (e.shiftKey && lastIdxRef.current !== null) {
      const [lo, hi] = [Math.min(lastIdxRef.current, idx), Math.max(lastIdxRef.current, idx)];
      const rangeIds = sorted.slice(lo, hi + 1).map(r => r.id);
      setSelected(prev => { const n = new Set(prev); rangeIds.forEach(id => n.add(id)); return n; });
    } else {
      toggleSelect(row.id);
    }
    lastIdxRef.current = idx;
  };
  const toggleAll = () => setSelected(prev => prev.size === sorted.length ? new Set() : new Set(sorted.map(r => r.id)));

  const commit = async (row, col, val, trigger) => {
    await onCommit(row.id, col, val);
    nav.settleAfterCommit(row.id, col, trigger === "enter");
  };

  const staffOptions = [{ value: "", label: "— Unassigned" }, ...staff.map(s => ({ value: String(s.id), label: s.name }))];
  const editOpts = canEdit ? {
    client_code: { type: "text" }, name: { type: "text" }, pan: { type: "text" },
    mobile: { type: "text" }, email: { type: "text" },
    entity_type: { type: "select", options: ENTITY_TYPES },
    assigned_staff_id: { type: "select", options: staffOptions },
    status: { type: "select", options: STATUSES },
  } : {};

  const display = {
    client_code: row => <span className="font-mono text-xs font-bold text-[#1e3a5f] bg-blue-50 px-2 py-0.5 rounded">{row.client_code || "—"}</span>,
    name: row => <span className="font-semibold text-[#1e3a5f]">{row.name}</span>,
    entity_type: row => <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">{row.entity_type || "—"}</span>,
    pan: row => <span className="font-mono text-xs">{row.pan || <span className="text-gray-300">—</span>}</span>,
    mobile: row => <span className="text-xs">{row.mobile || <span className="text-gray-300">—</span>}</span>,
    email: row => <span className="text-xs">{row.email || <span className="text-gray-300">—</span>}</span>,
    assigned_staff_id: row => <span className="text-xs text-gray-600">{row.staff_name || <span className="text-gray-300">—</span>}</span>,
    status: row => <StatusBadge status={row.status} />,
  };

  const cellProps = (row, col) => ({
    row, col, editOptions: editOpts[col] || null, displayValue: display[col](row),
    isActive: nav.isCellActive(row.id, col), isEditing: nav.isCellEditing(row.id, col),
    onActivate: () => nav.activate(row.id, col), onStartEdit: () => nav.startEdit(row.id, col),
    onCommit: (c, v, trigger) => commit(row, c, v, trigger), onCancel: nav.cancelEdit,
  });

  return (
    <div ref={nav.containerRef} tabIndex={0} onKeyDown={nav.handleKeyDown} className="outline-none overflow-x-auto">
      <table className="w-full text-left">
        <thead>
          <tr className="border-b border-gray-100">
            {showSelect && (
              <th className="table-header w-8">
                <input type="checkbox" checked={sorted.length > 0 && selected.size === sorted.length} onChange={toggleAll} className="rounded" />
              </th>
            )}
            {GRID_COLS.map(col => (
              <th key={col} className="table-header cursor-pointer select-none whitespace-nowrap" onClick={() => onSort(col)}>
                {COL_LABELS[col]}{sortKey === col && <span className="text-[#f0a500] ml-1">{sortDir === "asc" ? "↑" : "↓"}</span>}
              </th>
            ))}
            <th className="table-header w-8" />
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 && (
            <tr><td colSpan={GRID_COLS.length + 2} className="text-center py-10 text-sm text-gray-400">No clients found.</td></tr>
          )}
          {sorted.map((row, idx) => (
            <tr key={row.id} className="border-b border-gray-50 hover:bg-gray-50/50">
              {showSelect && (
                <td className="table-cell w-8">
                  <input type="checkbox" checked={selected.has(row.id)} onChange={() => {}}
                    onClick={e => handleCheckboxClick(e, row, idx)}
                    onMouseDown={e => e.shiftKey && e.preventDefault()}
                    className="rounded" />
                </td>
              )}
              {GRID_COLS.map(col => <EditableCell key={col} {...cellProps(row, col)} />)}
              <td className="table-cell w-8">
                <Link to={`/clients/${row.id}`} title="Open client"><Eye size={16} className="text-gray-400 hover:text-[#1e3a5f]" /></Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Bulk Update dialog: tick which fields to change, untouched fields stay ─────
const BULK_FIELDS = [
  { key: "assigned_staff_id", label: "Assigned Staff", type: "staff" },
  { key: "status", label: "Status", type: "select", options: STATUSES },
  { key: "entity_type", label: "Entity Type", type: "select", options: ENTITY_TYPES },
  { key: "constitution", label: "Constitution", type: "select", options: CONSTITUTIONS },
  { key: "mobile", label: "Mobile", type: "text" },
  { key: "email", label: "Email", type: "text" },
  { key: "whatsapp", label: "WhatsApp", type: "text" },
  { key: "client_since", label: "Client Since", type: "date" },
  { key: "referring_ca_id", label: "Billed via (Referring CA)", type: "rca" },
  { key: "notes", label: "Notes", type: "textarea" },
];

function BulkUpdateModal({ count, staff, referringCAs, onApply, onClose }) {
  const [ticked, setTicked] = useState({});
  const [values, setValues] = useState({});
  const setVal = (k, v) => setValues(p => ({ ...p, [k]: v }));

  const apply = () => {
    const updates = {};
    BULK_FIELDS.forEach(f => {
      if (!ticked[f.key]) return;
      let v = values[f.key];
      if (v === undefined || v === "") v = null;
      if ((f.key === "assigned_staff_id" || f.key === "referring_ca_id") && v !== null) v = parseInt(v);
      updates[f.key] = v;
    });
    if (!Object.keys(updates).length) { toast.error("Tick at least one field to update"); return; }
    onApply(updates);
  };

  const input = f => {
    const dis = !ticked[f.key];
    const cls = `input-field flex-1 ${dis ? "opacity-40" : ""}`;
    if (f.type === "staff") return (
      <select disabled={dis} value={values[f.key] ?? ""} onChange={e => setVal(f.key, e.target.value)} className={cls}>
        <option value="">— Unassigned</option>
        {staff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
      </select>
    );
    if (f.type === "rca") return (
      <select disabled={dis} value={values[f.key] ?? ""} onChange={e => setVal(f.key, e.target.value)} className={cls}>
        <option value="">Direct billing (default)</option>
        {referringCAs.map(rc => <option key={rc.id} value={rc.id}>{rc.name}</option>)}
      </select>
    );
    if (f.type === "select") return (
      <select disabled={dis} value={values[f.key] ?? ""} onChange={e => setVal(f.key, e.target.value)} className={cls}>
        <option value="">— Select —</option>
        {f.options.map(o => <option key={o}>{o}</option>)}
      </select>
    );
    if (f.type === "textarea") return <textarea disabled={dis} rows={2} value={values[f.key] ?? ""} onChange={e => setVal(f.key, e.target.value)} className={cls} />;
    return <input disabled={dis} type={f.type} value={values[f.key] ?? ""} onChange={e => setVal(f.key, e.target.value)} className={cls} />;
  };

  return (
    <Modal title={`Bulk Update ${count} Client${count !== 1 ? "s" : ""}`} onClose={onClose} size="lg">
      <div className="space-y-3">
        <p className="text-sm text-gray-500">Tick the fields to change. Everything left unticked stays exactly as it is on every selected client.</p>
        {BULK_FIELDS.map(f => (
          <label key={f.key} className="flex items-center gap-3">
            <input type="checkbox" checked={!!ticked[f.key]} onChange={e => setTicked(p => ({ ...p, [f.key]: e.target.checked }))} className="rounded flex-shrink-0" />
            <span className={`text-sm w-44 flex-shrink-0 ${ticked[f.key] ? "text-gray-800 font-medium" : "text-gray-500"}`}>{f.label}</span>
            {input(f)}
          </label>
        ))}
        <div className="flex gap-3 pt-3 border-t border-gray-100">
          <button onClick={apply} className="btn-primary flex-1">Apply to {count} Client{count !== 1 ? "s" : ""}</button>
          <button onClick={onClose} className="btn-secondary flex-1">Cancel</button>
        </div>
      </div>
    </Modal>
  );
}

export default function ClientsPage() {
  const { user, can } = useAuth();
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [loading, setLoading] = useState(true);
  const [staff, setStaff] = useState([]);
  const [referringCAs, setReferringCAs] = useState([]);
  const [form, setForm] = useState({ client_code: "", name: "", pan: "", aadhaar: "", tan: "", cin: "", llpin: "", entity_type: "Individual", constitution: "Proprietorship", mobile: "", email: "", whatsapp: "", address_registered: "", address_communication: "", services: [], assigned_staff_id: "", status: "Active", notes: "", client_since: "", gstins: [{ gstin: "", state: "" }] });

  // Bulk state
  const [selected, setSelected] = useState(new Set());
  const [bulkAssignVal, setBulkAssignVal] = useState("");
  const [bulkUpdateModal, setBulkUpdateModal] = useState(false);
  const [bulkModal, setBulkModal] = useState(false);
  const [bulkCheckDetails, setBulkCheckDetails] = useState([]);
  const [confirmText, setConfirmText] = useState("");
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkDeleteModal, setBulkDeleteModal] = useState(false);
  const [bulkDeleteConfirmText, setBulkDeleteConfirmText] = useState("");
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);

  const canAdmin = user && ["partner", "senior_staff", "super_admin", "admin"].includes(user.role);
  const canEdit = can("clients", "edit");
  const canBulk = can("clients", "bulk");

  const load = () => {
    const params = {};
    if (search) params.search = search;
    if (statusFilter) params.status = statusFilter;
    api.get("/clients", { params }).then(r => setClients(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { api.get("/auth/users").then(r => setStaff(r.data)); }, []);
  // Referring CAs feed the "Billed via" dropdowns (billing-gated; some roles get 403 — fine, list stays empty)
  useEffect(() => { api.get("/referring-cas").then(r => setReferringCAs(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { load(); }, [search, statusFilter]);

  // THE instant-update pattern: PATCH one field, patch the same field into local
  // state (patchRows re-derives staff_name from assigned_staff_id) — no refetch.
  const handleInlineCommit = async (id, col, val) => {
    let v = typeof val === "string" ? val.trim() : val;
    if (v === "") v = null;
    if (col === "name" && !v) { toast.error("Name cannot be empty"); throw new Error("empty"); }
    if (col === "client_code" && !v) { toast.error("Client Code is required"); throw new Error("empty"); }
    if (col === "pan" && v) v = v.toUpperCase();
    if (col === "assigned_staff_id") v = v ? parseInt(v) : null;
    try {
      await api.patch(`/clients/${id}`, { [col]: v });
    } catch (e) {
      toast.error(e?.response?.data?.error || "Save failed");
      throw e;
    }
    setClients(prev => patchRows(prev, id, { [col]: v }, { staff }));
  };

  // One batched request for all selected rows, then the same local patch
  const applyBulkUpdates = async updates => {
    const ids = Array.from(selected);
    try {
      const r = await api.post("/clients/bulk-update", { ids, updates });
      setClients(prev => patchRows(prev, ids, updates, { staff }));
      toast.success(`Updated ${r.data.updated} client${r.data.updated !== 1 ? "s" : ""}`);
      setBulkUpdateModal(false); setBulkAssignVal(""); setSelected(new Set());
    } catch (e) { toast.error(e?.response?.data?.error || "Bulk update failed"); }
  };

  const handleSave = async e => {
    e.preventDefault();
    try {
      const data = { ...form, gstins: form.gstins.filter(g => g.gstin) };
      await api.post("/clients", data);
      toast.success("Client added");
      setShowModal(false);
      setForm({ client_code: "", name: "", pan: "", aadhaar: "", tan: "", cin: "", llpin: "", entity_type: "Individual", constitution: "Proprietorship", mobile: "", email: "", whatsapp: "", address_registered: "", address_communication: "", services: [], assigned_staff_id: "", status: "Active", notes: "", client_since: "", gstins: [{ gstin: "", state: "" }] });
      load();
    } catch (e) { toast.error(e?.response?.data?.error || "Failed to save"); }
  };

  const handleBulkDeletePermanent = async () => {
    if (bulkDeleteConfirmText !== "DELETE") { toast.error("Type DELETE to confirm"); return; }
    setBulkDeleteLoading(true);
    try {
      const ids = Array.from(selected);
      const r = await api.post("/clients/bulk-delete-permanent", { ids });
      toast.success(`${r.data.deleted} client(s) permanently deleted`);
      setBulkDeleteModal(false);
      setSelected(new Set());
      load();
    } catch {
      toast.error("Permanent delete failed");
    } finally {
      setBulkDeleteLoading(false);
    }
  };

  const toggleService = svc => {
    setForm(f => ({ ...f, services: f.services.includes(svc) ? f.services.filter(s => s !== svc) : [...f.services, svc] }));
  };

  const handleBulkArchiveClick = async () => {
    const ids = Array.from(selected);
    try {
      const r = await api.post("/clients/bulk-archive-check", { ids });
      setBulkCheckDetails(r.data.details);
      setConfirmText("");
      setBulkModal(true);
    } catch {
      toast.error("Failed to check clients");
    }
  };

  const handleBulkArchiveConfirm = async () => {
    if (confirmText !== "ARCHIVE") { toast.error('Type ARCHIVE to confirm'); return; }
    setBulkLoading(true);
    try {
      const ids = Array.from(selected);
      const r = await api.post("/clients/bulk-archive", { ids, reason: "Bulk archived" });
      toast.success(r.data.archived + " clients archived successfully");
      setBulkModal(false);
      setSelected(new Set());
      load();
    } catch {
      toast.error("Bulk archive failed");
    } finally {
      setBulkLoading(false);
    }
  };

  return (
    <div>
      <PageHeader title="Client Master" subtitle={`${clients.length} clients`}
        actions={
          <div className="flex gap-2">
            <button onClick={() => downloadExport('/export/clients', {}, `KUA_Clients_${new Date().toISOString().split('T')[0]}.xlsx`)} className="btn-secondary flex items-center gap-2"><Download size={16} /> Export Excel</button>
            <button onClick={() => setShowImport(true)} className="btn-secondary flex items-center gap-2"><Upload size={16} /> Import Clients</button>
            <button onClick={() => setShowModal(true)} className="btn-primary flex items-center gap-2"><Plus size={16} /> Add Client</button>
          </div>
        } />

      <div className="card">
        <div className="flex flex-wrap gap-3 mb-4">
          <SearchBar value={search} onChange={setSearch} placeholder="Search name, PAN, mobile, code..." />
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="input-field w-36">
            <option value="">All Status</option>
            {STATUSES.map(s => <option key={s}>{s}</option>)}
          </select>
        </div>

        {/* Bulk action bar — appears when rows are selected */}
        {selected.size > 0 && (
          <div className="flex flex-wrap items-center gap-3 mb-4 px-4 py-2.5 bg-[#1e3a5f] rounded-xl text-white text-sm">
            <span className="font-medium">{selected.size} client{selected.size !== 1 ? "s" : ""} selected</span>
            {canBulk && (
              <>
                <div className="flex items-center gap-1.5">
                  <UserCheck size={15} className="opacity-70" />
                  <select value={bulkAssignVal} onChange={e => setBulkAssignVal(e.target.value)}
                    className="text-sm border-0 rounded-lg px-2 py-1.5 bg-white text-gray-800">
                    <option value="">Assign employee…</option>
                    {staff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    <option value="__UNASSIGN__">— Unassign</option>
                  </select>
                  {bulkAssignVal && (
                    <button onClick={() => applyBulkUpdates({ assigned_staff_id: bulkAssignVal === "__UNASSIGN__" ? null : parseInt(bulkAssignVal) })}
                      className="px-3 py-1.5 bg-[#f0a500] text-[#1e3a5f] text-xs font-semibold rounded-lg">Apply</button>
                  )}
                </div>
                <button onClick={() => setBulkUpdateModal(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-lg text-xs font-medium transition-colors">
                  <Pencil size={13} /> Bulk Update
                </button>
              </>
            )}
            {canAdmin && (
              <button onClick={handleBulkArchiveClick}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-500 hover:bg-orange-600 rounded-lg text-xs font-medium transition-colors">
                <Archive size={13} /> Archive
              </button>
            )}
            {canAdmin && (
              <button onClick={() => { setBulkDeleteConfirmText(""); setBulkDeleteModal(true); }}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-red-700 hover:bg-red-800 rounded-lg text-xs font-medium transition-colors">
                <Trash2 size={13} /> Delete
              </button>
            )}
            <button onClick={() => setSelected(new Set())} className="ml-auto flex items-center gap-1 text-xs opacity-70 hover:opacity-100">
              <X size={13} /> Clear
            </button>
          </div>
        )}

        {loading ? (
          <div className="text-center py-16 text-gray-400">Loading...</div>
        ) : (
          <ClientGrid clients={clients} staff={staff} canEdit={canEdit}
            showSelect={canBulk || canAdmin} selected={selected} setSelected={setSelected}
            onCommit={handleInlineCommit} />
        )}
      </div>

      {bulkUpdateModal && (
        <BulkUpdateModal count={selected.size} staff={staff} referringCAs={referringCAs}
          onApply={applyBulkUpdates} onClose={() => setBulkUpdateModal(false)} />
      )}

      {showImport && (
        <Modal title="Import Clients from Excel / CSV" onClose={() => setShowImport(false)} size="lg">
          <ClientImport onClose={() => setShowImport(false)} onImported={() => { load(); }} />
        </Modal>
      )}

      {showModal && (
        <Modal title="Add New Client" onClose={() => setShowModal(false)} size="xl">
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField label="Client Code *" required><input required value={form.client_code} onChange={e => setForm(f => ({ ...f, client_code: e.target.value }))} className="input-field font-mono" placeholder="e.g. KUA-001 or CLIENT-A1" /></FormField>
              <FormField label="Name" required><input required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="input-field" /></FormField>
              <FormField label="Entity Type"><select value={form.entity_type} onChange={e => setForm(f => ({ ...f, entity_type: e.target.value }))} className="input-field">{ENTITY_TYPES.map(t => <option key={t}>{t}</option>)}</select></FormField>
              <FormField label="PAN"><input value={form.pan} onChange={e => setForm(f => ({ ...f, pan: e.target.value.toUpperCase() }))} className="input-field" placeholder="ABCDE1234F" maxLength={10} /></FormField>
              <FormField label="Aadhaar"><input value={form.aadhaar} onChange={e => setForm(f => ({ ...f, aadhaar: e.target.value }))} className="input-field" /></FormField>
              <FormField label="TAN"><input value={form.tan} onChange={e => setForm(f => ({ ...f, tan: e.target.value.toUpperCase() }))} className="input-field" /></FormField>
              <FormField label="CIN / LLPIN"><input value={form.cin} onChange={e => setForm(f => ({ ...f, cin: e.target.value }))} className="input-field" /></FormField>
              <FormField label="Mobile" required><input required value={form.mobile} onChange={e => setForm(f => ({ ...f, mobile: e.target.value }))} className="input-field" /></FormField>
              <FormField label="Email"><input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="input-field" /></FormField>
              <FormField label="Assigned Staff"><select value={form.assigned_staff_id} onChange={e => setForm(f => ({ ...f, assigned_staff_id: e.target.value }))} className="input-field"><option value="">Select</option>{staff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</select></FormField>
              <FormField label="Billed via (Referring CA)"><select value={form.referring_ca_id || ""} onChange={e => setForm(f => ({ ...f, referring_ca_id: e.target.value ? parseInt(e.target.value) : null }))} className="input-field"><option value="">Direct billing (default)</option>{referringCAs.map(rc => <option key={rc.id} value={rc.id}>{rc.name}</option>)}</select></FormField>
              <FormField label="Status"><select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} className="input-field">{STATUSES.map(s => <option key={s}>{s}</option>)}</select></FormField>
              <FormField label="Client Since"><input type="date" value={form.client_since} onChange={e => setForm(f => ({ ...f, client_since: e.target.value }))} className="input-field" /></FormField>
            </div>
            <FormField label="Registered Address"><textarea value={form.address_registered} onChange={e => setForm(f => ({ ...f, address_registered: e.target.value }))} className="input-field" rows={2} /></FormField>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Services Subscribed</label>
              <div className="flex flex-wrap gap-2">
                {SERVICES.map(svc => (
                  <button key={svc} type="button" onClick={() => toggleService(svc)}
                    className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${form.services.includes(svc) ? "bg-[#1e3a5f] text-white border-[#1e3a5f]" : "text-gray-600 border-gray-300 hover:border-[#1e3a5f]"}`}>
                    {svc}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">GSTIN(s)</label>
              {form.gstins.map((g, i) => (
                <div key={i} className="flex gap-2 mb-2">
                  <input value={g.gstin} onChange={e => { const gs = [...form.gstins]; gs[i].gstin = e.target.value; setForm(f => ({ ...f, gstins: gs })); }} className="input-field flex-1" placeholder="GSTIN" maxLength={15} />
                  <input value={g.state} onChange={e => { const gs = [...form.gstins]; gs[i].state = e.target.value; setForm(f => ({ ...f, gstins: gs })); }} className="input-field w-36" placeholder="State" />
                </div>
              ))}
              <button type="button" onClick={() => setForm(f => ({ ...f, gstins: [...f.gstins, { gstin: "", state: "" }] }))} className="text-xs text-[#1e3a5f] hover:text-[#f0a500]">+ Add GSTIN</button>
            </div>
            <FormField label="Notes"><textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className="input-field" rows={2} /></FormField>
            <div className="flex gap-3 pt-2">
              <button type="submit" className="btn-primary flex-1">Save Client</button>
              <button type="button" onClick={() => setShowModal(false)} className="btn-secondary flex-1">Cancel</button>
            </div>
          </form>
        </Modal>
      )}

      {bulkDeleteModal && (
        <Modal title="⚠️ Permanent Deletion Warning" onClose={() => setBulkDeleteModal(false)} size="md">
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-300 rounded-lg p-4">
              <div className="flex items-start gap-2 mb-2">
                <AlertTriangle size={18} className="text-red-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm font-semibold text-red-700">This will COMPLETELY and PERMANENTLY delete {selected.size} client(s) and ALL related data:</p>
              </div>
              <ul className="text-xs text-red-600 space-y-0.5 ml-6 list-disc">
                <li>All ITR/TDS/GST/Audit/ROC/ESI records</li>
                <li>All tasks, documents, invoices</li>
                <li>All credentials and member details</li>
                <li>All ledger entries and recurring work</li>
              </ul>
              <p className="text-xs font-bold text-red-700 mt-3">THIS CANNOT BE UNDONE. There is no restore option for permanent deletion.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type <strong className="text-red-600">DELETE</strong> to confirm</label>
              <input value={bulkDeleteConfirmText} onChange={e => setBulkDeleteConfirmText(e.target.value)}
                className="input-field font-mono tracking-widest" placeholder="DELETE" autoComplete="off" />
            </div>
            <div className="flex gap-3">
              <button onClick={handleBulkDeletePermanent} disabled={bulkDeleteConfirmText !== "DELETE" || bulkDeleteLoading}
                className="flex-1 bg-red-700 hover:bg-red-800 disabled:opacity-40 text-white py-2 rounded-lg font-medium text-sm transition-colors">
                {bulkDeleteLoading ? "Deleting..." : `Confirm Permanent Delete (${selected.size})`}
              </button>
              <button onClick={() => setBulkDeleteModal(false)} className="btn-secondary flex-1">Cancel</button>
            </div>
          </div>
        </Modal>
      )}

      {bulkModal && (
        <Modal title={`Archive ${selected.size} Clients`} onClose={() => setBulkModal(false)} size="md">
          <div className="space-y-4">
            <p className="text-sm text-gray-600">You are about to archive the following clients:</p>
            <div className="max-h-52 overflow-y-auto space-y-2 border rounded-lg p-3">
              {bulkCheckDetails.map(c => (
                <div key={c.id} className="flex items-start gap-3 text-sm">
                  <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${(c.pendingTasks + c.unpaidInvoices) === 0 ? "bg-green-500" : "bg-orange-500"}`} />
                  <div>
                    <span className="font-medium text-gray-800">{c.name}</span>
                    {(c.pendingTasks + c.unpaidInvoices) === 0
                      ? <span className="ml-2 text-xs text-green-600">— safe to archive</span>
                      : <span className="ml-2 text-xs text-orange-600">— {c.pendingTasks} pending task{c.pendingTasks !== 1 ? "s" : ""}, {c.unpaidInvoices} unpaid invoice{c.unpaidInvoices !== 1 ? "s" : ""}</span>
                    }
                  </div>
                </div>
              ))}
            </div>
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm text-orange-700">
              Clients with pending items will be force-archived. All their pending work will be hidden from all modules.
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type <strong>ARCHIVE</strong> to confirm</label>
              <input value={confirmText} onChange={e => setConfirmText(e.target.value.toUpperCase())}
                className="input-field font-mono tracking-widest" placeholder="ARCHIVE" />
            </div>
            <div className="flex gap-3">
              <button onClick={handleBulkArchiveConfirm} disabled={confirmText !== "ARCHIVE" || bulkLoading}
                className="flex-1 bg-red-600 hover:bg-red-700 disabled:opacity-40 text-white py-2 rounded-lg font-medium text-sm transition-colors">
                {bulkLoading ? "Archiving..." : `Archive ${selected.size} Clients`}
              </button>
              <button onClick={() => setBulkModal(false)} className="btn-secondary flex-1">Cancel</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
